import { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { LifeBuoy, Mail, Send, Plus, MessageSquare, Loader2 } from "lucide-react";
import { toast } from "sonner";

const FAQ = [
  { q: "How do I upgrade to full access?", a: "Go to the Upgrade page from your dashboard, choose Full Access, and complete checkout with Stripe. Full Access is $49.95 per year and renews automatically until you cancel. Your content unlocks instantly." },
  { q: "How does the read + listen + practice method work?", a: "Each step engages a different part of the brain. Reading activates your visual and language centers, the narrated audio engages a separate auditory system, and practice questions force active recall — the single most powerful way to lock information into long-term memory. Spacing your study across short daily sessions beats cramming." },
  { q: "Why aren't the practice questions identical to the real exam?", a: "By design. Our questions are worded a little differently from the actual exam so you learn the concept instead of memorizing a specific question. If you can answer our variations, you truly understand the material and will recognize it however the state phrases it on test day." },
  { q: "Should I also buy a textbook?", a: "If you want to learn every detail of insurance policies and claims, a comprehensive textbook is a good idea and useful for your career. Our program is inexpensive and focused on the key, testable core concepts. Note that the licensing exam may include occasional questions our focused course doesn't cover, so a textbook is a helpful supplement if you want to close every gap." },
  { q: "Does this count toward the required 20 hours of pre-licensing?", a: "Not at this time. We're a study aid, not a state-approved pre-licensing course, so it doesn't currently satisfy the 20-hour requirement. We do plan to pursue course approval in the future." },
  { q: "Can I cancel, and what happens to my access?", a: "Yes — cancel auto-renew anytime from your account in one click. You keep full access through the end of the year you've already paid for; after that, premium content locks back to the free sample tier and your progress is preserved." },
  { q: "Can I use the workbook offline?", a: "Your workbook lives in an easy in-app reader, so it's always with you when you're signed in — read it anytime on your phone, tablet, or computer." },
  { q: "Does my audio remember where I left off?", a: "Yes — playback position is saved automatically, so you can resume any lesson from where you stopped." },
];

export const STATUS_META = {
  open: { label: "Open", cls: "bg-blue-100 text-blue-700 hover:bg-blue-100" },
  in_progress: { label: "In Progress", cls: "bg-amber-100 text-amber-700 hover:bg-amber-100" },
  waiting_customer: { label: "Waiting on You", cls: "bg-purple-100 text-purple-700 hover:bg-purple-100" },
  resolved: { label: "Resolved", cls: "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" },
};
const fmt = (d) => (d ? new Date(d).toLocaleString() : "");

export default function Support() {
  const { user } = useAuth();
  const authed = !!user && user !== false;
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const loadTickets = useCallback(async () => {
    if (!authed) return;
    try { const { data } = await api.get("/support/tickets"); setTickets(data); } catch { /* ignore */ }
  }, [authed]);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/support", form);
      toast.success(authed ? "Request submitted — we'll reply by email and here." : "Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
      setShowForm(false);
      loadTickets();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const openTicket = async (t) => {
    setActive(t); setThread(null);
    try { const { data } = await api.get(`/support/tickets/${t.id}`); setThread(data); loadTickets(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/support/tickets/${active.id}/reply`, { message: reply.trim() });
      setReply("");
      const { data } = await api.get(`/support/tickets/${active.id}`); setThread(data);
      toast.success("Reply sent");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center gap-3 mb-2">
          <LifeBuoy className="h-7 w-7 text-amber-600" />
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A192F]">Help Center</h1>
        </div>
        <p className="text-slate-500 mb-8">Find quick answers below, or contact support with a message.</p>

        <Accordion type="single" collapsible className="space-y-3 mb-12" data-testid="support-faq">
          {FAQ.map((f, i) => (
            <AccordionItem key={i} value={`f-${i}`} className="border border-slate-200 rounded-xl bg-white px-5">
              <AccordionTrigger className="text-left font-semibold text-[#0A192F] hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {authed ? (
          <div data-testid="my-support-requests">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold text-[#0A192F] flex items-center gap-2"><MessageSquare className="h-5 w-5 text-amber-600" /> My Support Requests</h2>
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => setShowForm((s) => !s)} data-testid="support-new-request-btn"><Plus className="h-4 w-4 mr-1" /> New Request</Button>
            </div>

            {showForm && (
              <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 mb-6 space-y-4" data-testid="support-new-form">
                <div><Label>Subject</Label><Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1.5" data-testid="support-subject" /></div>
                <div><Label>How can we help?</Label><Textarea required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5" data-testid="support-message" /></div>
                <Button type="submit" disabled={loading} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="support-submit-btn"><Send className="h-4 w-4 mr-2" /> {loading ? "Sending…" : "Submit Request"}</Button>
              </form>
            )}

            <div className="rounded-2xl border border-slate-200 bg-white divide-y" data-testid="support-ticket-list">
              {tickets.length === 0 ? <div className="p-6 text-slate-400 text-sm">No requests yet. Tap "New Request" to reach the team.</div> : tickets.map((t) => (
                <button key={t.id} onClick={() => openTicket(t)} className="w-full text-left p-4 flex items-center justify-between gap-3 hover:bg-amber-50/50" data-testid={`support-ticket-${t.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-[#0A192F] truncate flex items-center gap-2">{t.subject}{t.unread_by_user && <span className="h-2 w-2 rounded-full bg-amber-500" title="New reply" />}</div>
                    <div className="text-xs text-slate-400">{fmt(t.last_reply_at)} · {t.message_count} message{t.message_count === 1 ? "" : "s"}</div>
                  </div>
                  <Badge className={STATUS_META[t.status]?.cls}>{STATUS_META[t.status]?.label || t.status}</Badge>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
            <h2 className="font-heading text-xl font-bold text-[#0A192F] flex items-center gap-2"><Mail className="h-5 w-5 text-amber-600" /> Contact Support</h2>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="support-name" /></div>
                <div><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="support-email" /></div>
              </div>
              <div><Label>Subject</Label><Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1.5" data-testid="support-subject" /></div>
              <div><Label>Message</Label><Textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5" data-testid="support-message" /></div>
              <Button type="submit" disabled={loading} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="support-submit-btn"><Send className="h-4 w-4 mr-2" /> {loading ? "Sending…" : "Send Message"}</Button>
            </form>
          </div>
        )}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => { if (!o) { setActive(null); setThread(null); } }}>
        <DialogContent className="max-w-lg" data-testid="support-thread-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 pr-6">{active?.subject} {active && <Badge className={STATUS_META[active.status]?.cls}>{STATUS_META[active.status]?.label}</Badge>}</DialogTitle></DialogHeader>
          {!thread ? <div className="py-8 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div> : (
            <>
              <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1" data-testid="support-thread-messages">
                {thread.messages.map((m) => (
                  <div key={m.id} className={`rounded-lg p-3 text-sm ${m.author === "admin" ? "bg-amber-50 border border-amber-100" : "bg-slate-50 border border-slate-100"}`}>
                    <div className="text-xs text-slate-400 mb-1">{m.author === "admin" ? "Support Team" : "You"} · {fmt(m.created_at)}</div>
                    <div className="text-slate-700 whitespace-pre-wrap">{m.body}</div>
                  </div>
                ))}
              </div>
              {active?.status !== "resolved" && (
                <div className="mt-3">
                  <Textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply…" data-testid="support-reply-input" />
                  <Button className="mt-2 bg-amber-600 hover:bg-amber-700 text-white" disabled={busy || !reply.trim()} onClick={sendReply} data-testid="support-reply-send"><Send className="h-4 w-4 mr-1.5" /> Send Reply</Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
